/**
 * Choosing a new password from an emailed link.
 *
 * The link ends every session on the account, including this browser's, so
 * the screen sends them to sign in rather than pretending they are still
 * signed in somewhere.
 */
import { useState } from "react";
import type { FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Layout } from "../components/Layout";
import { useToast } from "../hooks/useToast";
import { auth as authApi } from "../api/endpoints";
import { validatePassword } from "../lib/validation";

export function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";
  const navigate = useNavigate();
  const toast = useToast();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const passwordError = validatePassword(password);
  // Checked here and nowhere else: the API has no second field to compare
  // against, and this is the only thing standing between a typo and being
  // locked out of the account they just recovered.
  const confirmError =
    confirm && confirm !== password ? "Both passwords need to match" : null;

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setTouched({ password: true, confirm: true });
    setError(null);
    if (passwordError || confirmError || !confirm) return;

    setSaving(true);
    try {
      await authApi.resetPassword(token, password);
      toast.show("Password changed. Sign in with the new one.", "success");
      navigate("/login", { replace: true });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  if (!token) {
    return (
      <Layout showNav={false} showAccount={false} title="Reset password">
        <section className="section">
          <div className="wrap narrow">
            <div className="panel empty">
              <h3>That link is incomplete</h3>
              <p className="muted" style={{ margin: "6px 0 0" }}>
                Some email apps cut long links in half. Open it from the email
                again, or ask for a new one.
              </p>
              <Link to="/forgot-password" className="btn btn-ghost mt-16">
                Send a new link
              </Link>
            </div>
          </div>
        </section>
      </Layout>
    );
  }

  return (
    <Layout showNav={false} showAccount={false} title="Reset password">
      <section className="section">
        <div className="wrap narrow">
          <div className="eyebrow">Contributor Network</div>
          <h1 className="display" style={{ fontSize: "clamp(26px,5vw,40px)" }}>
            Choose a new<br />password.
          </h1>
          <p className="lede">
            This also signs you out everywhere else — which is what you want if
            somebody else has been in your account.
          </p>

          <form className="panel" onSubmit={handleSubmit} noValidate>
            <div className={touched.password && passwordError ? "field err" : "field"}>
              <label htmlFor="new-password">New password</label>
              <input
                id="new-password"
                type="password"
                value={password}
                autoComplete="new-password"
                autoFocus
                onChange={(event) => setPassword(event.target.value)}
                onBlur={() => setTouched((t) => ({ ...t, password: true }))}
              />
              <div className="hint">At least 8 characters, with a letter and a number.</div>
              <div className="err-msg">{touched.password ? passwordError : ""}</div>
            </div>

            <div className={touched.confirm && confirmError ? "field err" : "field"}>
              <label htmlFor="confirm-password">Type it again</label>
              <input
                id="confirm-password"
                type="password"
                value={confirm}
                autoComplete="new-password"
                onChange={(event) => setConfirm(event.target.value)}
                onBlur={() => setTouched((t) => ({ ...t, confirm: true }))}
              />
              <div className="err-msg">{touched.confirm ? confirmError : ""}</div>
            </div>

            {error && (
              <>
                <div className="form-error">{error}</div>
                <Link to="/forgot-password" className="btn btn-ghost btn-sm mt-8">
                  Send a new link
                </Link>
              </>
            )}

            <button type="submit" className="btn btn-primary mt-8" disabled={saving}>
              {saving ? "Saving…" : "Change my password"}
            </button>
          </form>
        </div>
      </section>
    </Layout>
  );
}
